import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Zap, Gift, Percent, Tag } from 'lucide-react';
import { sliderDeals, stores } from '../data/mockData';
import DealDialog from './DealDialog';

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const slides = sliderDeals;

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [slides.length, isTransitioning]);

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsTransitioning(false), 600);
  };

  const goToSlide = (index) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 600);
  };

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide, isPaused]);

  const handleDealClick = (slide) => {
    const store = stores.find(s => s.slug === slide.store);
    const dealData = {
      id: slide.id,
      title: slide.title,
      image: getSlideStyle(slide).image || 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=400&fit=crop',
      discount: slide.discount ? parseInt(slide.discount) : (slide.price ? 15 : 20),
      originalPrice: slide.price ? parseFloat(slide.price.replace('$', '')) * 1.2 : 99.99,
      salePrice: slide.price ? parseFloat(slide.price.replace('$', '')) : 79.99,
      couponCode: slide.couponCode,
      cashback: slide.cashback || `Save with code ${slide.couponCode}`,
      store: slide.store,
      storeUrl: slide.storeUrl,
      storeName: store?.name,
    };
    setSelectedDeal(dealData);
    setDialogOpen(true);
  };

  const getSlideStyle = (slide) => {
    const styles = {
      casper: {
        bg: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
        image: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?w=800&h=500&fit=crop',
        icon: Zap,
      },
      purple: {
        bg: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
        image: null,
        icon: Gift,
      },
      tempurpedic: {
        bg: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        image: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&h=500&fit=crop',
        icon: Tag,
      },
      nectar: {
        bg: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
        image: 'https://images.unsplash.com/photo-1615874959474-d609969a20ed?w=800&h=500&fit=crop',
        icon: Percent,
      },
      saatva: {
        bg: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        image: 'https://images.unsplash.com/photo-1506439773649-6e0eb8cfb237?w=800&h=500&fit=crop',
        icon: Zap,
      },
      sleepnumber: {
        bg: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
        image: 'https://images.unsplash.com/photo-1631049552057-403cdb8f0658?w=800&h=500&fit=crop',
        icon: Gift,
      },
    };
    return styles[slide.type] || styles.casper;
  };

  const renderDesktopSlide = (slide, position) => {
    const style = getSlideStyle(slide);
    const isMain = position === 0;
    const IconComponent = style.icon;

    if (slide.type === 'purple') {
      return (
        <div
          onClick={() => handleDealClick(slide)}
          className={`relative h-72 md:h-80 rounded-3xl overflow-hidden flex items-center justify-center cursor-pointer transition-all duration-500 group ${
            isMain ? 'scale-100 shadow-2xl' : 'scale-[0.97] opacity-90'
          }`}
          style={{ background: style.bg }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
          <div className="text-center text-white p-6 z-10 relative">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <IconComponent className="w-8 h-8 text-violet-600" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-3xl md:text-4xl font-heading font-extrabold text-white">{slide.subtitle}</span>
            </div>
            <h2 className="text-xl md:text-2xl font-heading font-bold mb-2">{slide.title}</h2>
            <p className="text-sm md:text-base mb-4 text-white/80">{slide.description}</p>
            <button className="bg-white text-violet-600 px-6 py-3 rounded-full font-bold text-sm hover:bg-violet-100 transition-all duration-300 hover:scale-105">
              {slide.cashback}
            </button>
          </div>
          <div className="absolute bottom-4 left-4 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full text-white text-xs font-medium">
            Code: <span className="font-bold">{slide.couponCode}</span>
          </div>
        </div>
      );
    }

    return (
      <div
        onClick={() => handleDealClick(slide)}
        className={`relative h-72 md:h-80 rounded-3xl overflow-hidden cursor-pointer transition-all duration-500 group ${
          isMain ? 'scale-100 shadow-2xl' : 'scale-[0.97] opacity-90'
        }`}
        style={{
          backgroundImage: style.image ? `${style.bg}, url(${style.image})` : style.bg,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-6">
          <p className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-2">{slide.subtitle}</p>
          <h2 className="text-white text-2xl md:text-3xl font-heading font-bold mb-4">{slide.title}</h2>
          <button className="bg-white text-red-600 px-6 py-3 rounded-full font-bold text-sm hover:bg-red-50 transition-all duration-300 hover:scale-105 w-fit btn-press">
            {slide.buttonText || 'Get Deal'}
          </button>
        </div>
        <div className="absolute top-4 right-4 bg-white text-red-600 px-4 py-2 rounded-full font-black text-sm group-hover:scale-110 transition-transform">
          {slide.price || slide.discount}
        </div>
        <div className="absolute bottom-4 right-4 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full text-white text-xs font-medium">
          <span className="font-bold">{slide.couponCode}</span>
        </div>
      </div>
    );
  };

  const renderMobileSlide = (slide, isActive) => {
    const style = getSlideStyle(slide);
    const IconComponent = style.icon;

    if (slide.type === 'purple') {
      return (
        <div
          onClick={() => handleDealClick(slide)}
          className={`relative h-72 rounded-3xl overflow-hidden flex items-center justify-center cursor-pointer transition-all duration-700 ${
            isActive ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}
          style={{ background: style.bg }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className={`text-center text-white p-6 z-10 transition-all duration-500 delay-200 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4">
              <IconComponent className="w-7 h-7 text-violet-600" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-3xl font-heading font-extrabold text-white">{slide.subtitle}</span>
            </div>
            <h2 className="text-xl font-heading font-bold mb-2">{slide.title}</h2>
            <p className="text-sm mb-4 text-white/80">{slide.description}</p>
            <button className="bg-white text-violet-600 px-6 py-3 rounded-full font-bold text-sm hover:bg-violet-100 transition-colors">
              {slide.cashback}
            </button>
          </div>
          <div className="absolute bottom-4 left-4 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full text-white text-xs font-medium">
            Code: <span className="font-bold">{slide.couponCode}</span>
          </div>
        </div>
      );
    }

    return (
      <div
        onClick={() => handleDealClick(slide)}
        className={`relative h-72 rounded-3xl overflow-hidden cursor-pointer transition-all duration-700 ${
          isActive ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
        }`}
        style={{
          backgroundImage: style.image ? `${style.bg}, url(${style.image})` : style.bg,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className={`absolute inset-0 flex flex-col justify-end p-6 transition-all duration-500 delay-200 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-2">{slide.subtitle}</p>
          <h2 className="text-white text-2xl font-heading font-bold mb-4">{slide.title}</h2>
          <button className="bg-white text-red-600 px-6 py-3 rounded-full font-bold text-sm hover:bg-red-50 transition-colors w-fit">
            {slide.buttonText || 'Get Deal'}
          </button>
        </div>
        <div className={`absolute top-4 right-4 bg-white text-red-600 px-4 py-2 rounded-full font-black text-sm transition-all duration-300 delay-300 ${isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
          {slide.price || slide.discount}
        </div>
        <div className={`absolute bottom-4 right-4 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full text-white text-xs font-medium transition-all duration-300 delay-400 ${isActive ? 'opacity-100' : 'opacity-0'}`}>
          <span className="font-bold">{slide.couponCode}</span>
        </div>
      </div>
    );
  };

  const getVisibleSlides = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentSlide + i) % slides.length;
      visible.push({ slide: slides[index], position: i });
    }
    return visible;
  };

  return (
    <div className="relative px-4 md:px-8 py-8 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-500 rounded-xl flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Hot Sleep Deals</h2>
            <p className="text-slate-500 text-sm">Don't miss these limited-time mattress offers</p>
          </div>
        </div>

        {/* Desktop: Show 3 slides */}
        <div className="hidden lg:grid lg:grid-cols-3 gap-6">
          {getVisibleSlides().map(({ slide, position }, i) => (
            <div
              key={`desktop-${slide.id}-${i}`}
              className="transition-all duration-500 ease-out"
            >
              {renderDesktopSlide(slide, position)}
            </div>
          ))}
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:block">
          <button
            onClick={prevSlide}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white p-3 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-20 hover:scale-110 group"
            disabled={isTransitioning}
          >
            <ChevronLeft size={24} className="text-slate-700 group-hover:text-red-600 transition-colors" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white p-3 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-20 hover:scale-110 group"
            disabled={isTransitioning}
          >
            <ChevronRight size={24} className="text-slate-700 group-hover:text-red-600 transition-colors" />
          </button>
        </div>

        {/* Mobile carousel */}
        <div className="lg:hidden relative">
          <div className="overflow-hidden">
            <div className="relative">
              {slides.map((slide, index) => (
                <div
                  key={`mobile-${slide.id}`}
                  className={`transition-all duration-700 ease-out ${
                    index === currentSlide 
                      ? 'relative' 
                      : 'absolute inset-0 pointer-events-none'
                  }`}
                >
                  {renderMobileSlide(slide, index === currentSlide)}
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Navigation */}
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-20 hover:scale-110"
            disabled={isTransitioning}
          >
            <ChevronLeft size={20} className="text-slate-700" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-20 hover:scale-110"
            disabled={isTransitioning}
          >
            <ChevronRight size={20} className="text-slate-700" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center mt-6 gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`relative h-2.5 rounded-full transition-all duration-500 overflow-hidden ${
                index === currentSlide ? 'w-10 bg-red-600' : 'w-2.5 bg-slate-300 hover:bg-slate-400'
              }`}
            >
              {index === currentSlide && !isPaused && (
                <span 
                  className="absolute inset-0 bg-red-600 origin-left"
                  style={{
                    animation: 'progress 5s linear',
                  }}
                />
              )}
            </button>
          ))}
        </div>

        <style>{`
          @keyframes progress {
            from { transform: scaleX(0); }
            to { transform: scaleX(1); }
          }
        `}</style>
      </div>

      <DealDialog
        deal={selectedDeal}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        storeName={selectedDeal?.storeName}
      />
    </div>
  );
};

export default HeroSlider;
